import axios from "axios";
import { useMutation, useQuery, useQueryClient } from "react-query";

// -----------------------------
// QUERY KEYS
// -----------------------------

// ArcGIS
const ARCGIS_TOKEN_QUERY_KEY = "arcgisToken";

// Inspection lines
const INSPECTION_LINES_QUERY_KEY = "inspectionLines";
const ADMIN_INSPECTION_LINES_QUERY_KEY = "adminInspectionLines";
const INSPECTION_LINE_QUERY_KEY = "inspectionLine";

// Workshop
const WORKSHOP_ORDERS_QUERY_KEY = "workshopOrders";
const WORKSHOP_ORDER_QUERY_KEY = "workshopOrder";
const ACTIVE_WORKSHOP_LINE_QUERY_KEY = "activeWorkshopLine";

// Project line
const PROJECT_LINE_QUERY_KEY = "projectLine";
const PROJECT_LINE_LOCATION_QUERY_KEY = "projectLineLocation";

// Location
const LOCATION_QUERY_KEY = "projectLocation";
const LOCATION_IMAGES_QUERY_KEY = "projectLocation";

// Equipment & related
const EQUIPMENT_QUERY_KEY = "equipment";
const SPARE_PARTS_QUERY_KEY = "spareParts";
const WORKSHOP_PRODUCTS_QUERY_KEY = "workshopProducts";

// Picking lines
const PICKING_LINES_QUERY_KEY = "pickingLines";
const PICKING_LINE_QUERY_KEY = "pickingLine";
const PICKING_LINE_EXTRA_BOX_QUERY_KEY = "pickingLineExtraBox";
const PICKING_LINE_NEW_PRODUCTS_QUERY_KEY = "pickingLineNewProducts";
const STOCK_TALLY_QUERY_KEY = "stockTally";

// Other
const GROUPS_QUERY_KEY = "groups";
const MOWING_LINES_BY_PROJECT_ID_QUERY_KEY = "mowingLinesByProjectId";
const IMAGES_QUERY_KEY = "images";

// Gardening admin
const GARDENING_MANAGERS_QUERY_KEY = "gardeningManagers";

// Winterwork
const WINTER_WORK_LINES_QUERY_KEY = "winterworkLines";
const WINTER_WORK_SUBSCRIPTIONS_QUERY_KEY = "winterworkSubscriptions";
const WINTER_WORK_IMPORT_POSTAL_CODES_QUERY_KEY = "winterworkImportPostalCodes";

// -----------------------------
// API UTILITY FUNCTIONS
// -----------------------------
export async function apiGet(url) {
    const response = await axios.get(url);

    if (response.status !== 200 && response.status !== 204) {
        throw new Error(`GET Request failed with status code ${response.status}`);
    }

    return response.data;
}
async function apiPost(url, data) {
    const response = await axios.post(url, data);

    if (response.status !== 200 && response.status !== 201) {
        throw new Error(`POST Request failed with status code ${response.status}`);
    }

    return response.data;
}
export async function apiPut(url, data) {
    const response = await axios.put(url, data);

    if (response.status !== 200 && response.status !== 204) {
        throw new Error(`PUT Request failed with status code ${response.status}`);
    }

    return response.data;
}
async function apiDelete(url) {
    const response = await axios.delete(url);

    if (response.status !== 200) {
        throw new Error(`DELETE Request failed with status code ${response.status}`);
    }

    return response.data;
}

// -----------------------------
// GENERAL API HOOKS & FUNCTIONS
// -----------------------------
async function fetchAllGroups() {
    return apiGet(`/api/common/equipment-groups`);
}

export const useGetArcgisToken = (options = {}) => {
    return useQuery([ARCGIS_TOKEN_QUERY_KEY], () => apiGet(`/api/common/arcgis-token`), {
        staleTime: Infinity,
        onSuccess: () => {
            options?.onSuccess && options.onSuccess();
        },
        onError: () => {
            options?.onError && options.onError();
        },
        ...options,
    });
};
export const useGetImages = (folderName, options = {}) => {
    return useQuery([IMAGES_QUERY_KEY, folderName], () => apiGet(`/api/common/images/${folderName}`), {
        onSuccess: () => {
            options?.onSuccess && options.onSuccess();
        },
        onError: () => {
            options?.onError && options.onError();
        },
        ...options,
    });
};
export const useUploadImages = (options = {}) => {
    return useMutation(
        ({ FolderName, FileName, Metadata, Base64 }) =>
            apiPost(`/api/common/images`, { FolderName, FileName, Metadata, Base64 }),
        {
            onSuccess: () => {
                options.onSuccess && options.onSuccess();
            },
            onError: () => {
                options.onError && options.onError();
            },
            ...options,
        }
    );
};

export const useGetProjectLine = (projectLineId, options = {}) => {
    const queryClient = useQueryClient();
    return useQuery(
        [PROJECT_LINE_QUERY_KEY, projectLineId],
        () => apiGet(`/api/common/project-lines/${projectLineId}`),
        {
            onSuccess: (data) => {
                options?.onSuccess && options.onSuccess(data);
            },
            onError: () => {
                queryClient.invalidateQueries([PROJECT_LINE_QUERY_KEY]);
                options?.onError && options.onError();
            },
            ...options,
        }
    );
};

export const useUpdateProjectLineIssue = (options = {}) => {
    const queryClient = useQueryClient();
    return useMutation(
        ({ ProjectLine, Issue, TotalInGroup }) =>
            apiPut("/api/common/project-lines", { ProjectLine, Issue, TotalInGroup }),
        {
            onSuccess: () => {
                queryClient.invalidateQueries([PROJECT_LINE_QUERY_KEY]);
                options.onSuccess && options.onSuccess();
            },
            onError: () => {
                queryClient.invalidateQueries([PROJECT_LINE_QUERY_KEY]);
                options.onError && options.onError();
            },
            ...options,
        }
    );
};
export const useStartProjectLine = (options = {}) => {
    const queryClient = useQueryClient();

    return useMutation(
        ({ ProjectLine, Issue, TotalInGroup, Location, CheckIn, Worker }) =>
            apiPut("/api/common/project-lines", { ProjectLine, Issue, TotalInGroup, Location, CheckIn, Worker }),
        {
            onSuccess: () => {
                queryClient.invalidateQueries([PROJECT_LINE_QUERY_KEY]);
                options.onSuccess && options.onSuccess();
            },
            onError: () => {
                queryClient.invalidateQueries([PROJECT_LINE_QUERY_KEY]);
                options.onError && options.onError();
            },
            ...options,
        }
    );
};
export const useStopProjectLine = (options = {}) => {
    const queryClient = useQueryClient();
    return useMutation(
        ({ ProjectLine, Issue, TotalInGroup, Location, CheckOut, Worker }) =>
            apiPut("/api/common/project-lines", { ProjectLine, Issue, TotalInGroup, Location, CheckOut, Worker }),
        {
            onSuccess: () => {
                queryClient.invalidateQueries([PROJECT_LINE_QUERY_KEY]);
                options.onSuccess && options.onSuccess();
            },
            onError: () => {
                queryClient.invalidateQueries([PROJECT_LINE_QUERY_KEY]);
                options.onError && options.onError();
            },
            ...options,
        }
    );
};
export const useSkipProjectLine = (options = {}) => {
    const queryClient = useQueryClient();
    return useMutation(
        ({ ProjectLine, Issue, TotalInGroup, Location, DateSkipped, Worker }) =>
            apiPut("/api/common/project-lines", {
                ProjectLine,
                Issue,
                TotalInGroup,
                Location,
                DateSkipped,
                Worker,
                Skip: true,
            }),
        {
            onSuccess: () => {
                queryClient.invalidateQueries([PROJECT_LINE_QUERY_KEY]);
                options.onSuccess && options.onSuccess();
            },
            onError: () => {
                queryClient.invalidateQueries([PROJECT_LINE_QUERY_KEY]);
                options.onError && options.onError();
            },
            ...options,
        }
    );
};

export const useFetchAllGroupsQuery = () => {
    return useQuery([GROUPS_QUERY_KEY], () => fetchAllGroups());
};
export const useSubmitIncidentReportForm = (options = {}) => {
    return useMutation(
        ({
            ProjectLineId,
            LocationId,
            UserId,
            VehicleId,
            LocationName,
            IsDamageToEquipment,
            OwnerId, // kennitala
            OwnerPhone,
            DamageType,
            IsOwnerPresent,
            TimeOfIncident,
            ReportedToArekstur,
            UserDescription,
            Concluded,
            OwnerName,
        }) =>
            apiPost("/api/common/project-lines/incident-reports", {
                ProjectLineId,
                LocationId,
                UserId,
                VehicleId,
                LocationName,
                IsDamageToEquipment,
                OwnerId,
                OwnerPhone,
                DamageType,
                IsOwnerPresent,
                TimeOfIncident,
                ReportedToArekstur,
                UserDescription,
                Concluded,
                OwnerName,
            }),
        {
            onSuccess: () => {
                options.onSuccess && options.onSuccess();
            },
            onError: () => {
                options.onError && options.onError();
            },
            ...options,
        }
    );
};

// -----------------------------
// WINTERWORK API HOOKS & FUNCTIONS
// -----------------------------
async function fetchWinterWorkProjectLines() {
    const projectLines = await apiGet('/api/winterwork/GetWinterWorkProjectLines');

    // Sort lines by serial number
    projectLines.sort((a, b) => (a.serialNumber < b.serialNumber ? 1 : -1));

    // Get list numbers from lines
    const availableListsNumbers = [...new Set(projectLines.map(line => line.listId))].sort((a, b) => a - b);

    return { projectLines, availableListsNumbers };
};
async function fetchWinterWorkSubTypes() {
    // Fetch winter work subscriptions
    const subscriptions = await apiGet("/api/winterwork/GetWinterWorkSubscriptions");

    // Filter and map to get unique products for service type "Vetrarverk"
    const serviceSubTypes = subscriptions
        .filter(el => el.serviceType.name === "Vetrarverk" && el.product != null)
        .map(el => el.product)
        .reduce((accumulator, current) => {
            if (!accumulator.some(x => x.id === current.id)) {
                accumulator.push(current);
            }
            return accumulator;
        }, [])
        .sort((a, b) => a.name.localeCompare(b.name));

    return { serviceSubTypes };
};
async function fetchDistinctPostalCodes() {
    // Fetch distinct postal codes
    const postalCodes = await apiGet("/api/winterwork/GetDistinctPostalCodeFromAddress");
    return { importPostalCodes: postalCodes };
};
async function moveProjectLineToList({ projectLineId, selectedList }) {
    return apiPost("/api/winterwork/MoveProjectLineToList", {
        ProjectLineId: projectLineId,
        SelectedList: selectedList,
    });
};
async function createWinterProjects({ serviceType, selectedProduct, firstListNumber, numListsToCreate, selectedImportPostalCode }) {
    return apiPost("/api/winterwork/CreateWinterProjects", {
        ServiceType: serviceType,
        Product: selectedProduct,
        FirstListNumber: firstListNumber,
        NumberOfLists: numListsToCreate,
        PostalCode: selectedImportPostalCode
    });
};

// Queries
export const useGetWinterWorkProjectLines = (options = {}) => {
    return useQuery(
        [WINTER_WORK_LINES_QUERY_KEY],
        fetchWinterWorkProjectLines,
        { ...options }
    );
};
export const useGetWinterWorkSubtypes = (options = {}) => {
    return useQuery(
        [WINTER_WORK_SUBSCRIPTIONS_QUERY_KEY],
        fetchWinterWorkSubTypes,
        { ...options }
    );
};
export const useGetWinterImportPostalCodes = (options = {}) => {
    return useQuery(
        [WINTER_WORK_IMPORT_POSTAL_CODES_QUERY_KEY],
        fetchDistinctPostalCodes,
        { ...options }
    );
};

// Mutations
export const useMoveProjectLineToList = (options = {}) => {
    const queryClient = useQueryClient();

    return useMutation(({ projectLineId, selectedList }) => moveProjectLineToList({ projectLineId, selectedList }),
    {
        ...options,
        onSuccess: () => {
            queryClient.invalidateQueries([WINTER_WORK_LINES_QUERY_KEY]);
            options?.onSuccess && options.onSuccess();
        },
        onError: () => {
            queryClient.invalidateQueries([WINTER_WORK_LINES_QUERY_KEY]);
            options?.onError && options.onError();
        }
    });
};
export const useCreateWinterProjects = (options = {}) => {
    const queryClient = useQueryClient();

    return useMutation(({ serviceType, selectedProduct, firstListNumber, numListsToCreate, selectedImportPostalCode }) =>
        createWinterProjects({ serviceType, selectedProduct, firstListNumber, numListsToCreate, selectedImportPostalCode }),
        {
            ...options,
            onSuccess: () => {
                queryClient.invalidateQueries([WINTER_WORK_LINES_QUERY_KEY]);
                options?.onSuccess && options.onSuccess();
            },
            onError: () => {
                queryClient.invalidateQueries([WINTER_WORK_LINES_QUERY_KEY]);
                options?.onError && options.onError();
            }
        });
};

// -----------------------------
// LOCATION API HOOKS & FUNCTIONS
// -----------------------------
export const useGetLocation = (locationId, options = {}) => {
    return useQuery([LOCATION_QUERY_KEY, locationId], () => apiGet(`/api/common/locations/${locationId}`), {
        ...options,
    });
};
export const useGetLocationImages = (locationId, options = {}) => {
    return useQuery(
        [LOCATION_IMAGES_QUERY_KEY, locationId],
        () => apiGet(`/api/common/locations/${locationId}/images`),
        {
            ...options,
            onSuccess: () => {
                options?.onSuccess && options.onSuccess();
            },
            onError: () => {
                options?.onError && options.onError();
            }
        }
    );
};
export const useUploadLocationImages = (locationId, options = {}) => {
    const queryClient = useQueryClient();
    return useMutation(
        ({ FolderName, FileName, Metadata, Base64 }) =>
            apiPost(`/api/common/locations/${locationId}/images`, {
                FolderName,
                FileName,
                Metadata,
                Base64,
            }),
        {
            onSuccess: () => {
                queryClient.invalidateQueries([LOCATION_IMAGES_QUERY_KEY, locationId]);
                options.onSuccess && options.onSuccess();
            },
            onError: () => {
                options.onError && options.onError();
            },
            ...options,
        }
    );
};

// -----------------------------
// MOWING LINE HOOKS & FUNCTIONS
// -----------------------------
export const useGetMowingLine = (projectLineId, options = {}) => {
    const queryClient = useQueryClient();
    return useQuery(
        [PROJECT_LINE_QUERY_KEY, projectLineId],
        () => apiGet(`/api/mowing/GetMowingWorkProjectLineById/${projectLineId}`),
        {
            onSuccess: () => {
                options?.onSuccess && options.onSuccess();
            },
            onError: () => {
                queryClient.invalidateQueries([PROJECT_LINE_QUERY_KEY]);
                options?.onError && options.onError();
            },
            ...options,
        }
    );
};
export const useGetMowingLines = (projectId, options = {}) => {
    return useQuery(
        [MOWING_LINES_BY_PROJECT_ID_QUERY_KEY, projectId],
        () => apiGet(`/api/mowing/GetMowingWorkProjectLinesRegardingProjectId/${projectId}`),
        { ...options }
    );
};
export const useGetLocationByProjectLineId = (id, options = {}) => {
    const queryClient = useQueryClient();
    return useQuery(
        [PROJECT_LINE_LOCATION_QUERY_KEY, id],
        async () => {
            try {
                const workline = await apiGet(`/api/common/project-locations/${id}`);
                if (!workline || !workline.project || !workline.project.address) {
                    console.error("No workline data or incorrect data path.");
                    return {};
                }
                const location = workline.project.address;
                let data = { ...location };

                if (location?.id) {
                    const details = await apiGet(`/api/mowing/GetMowingAccountLocationDescriptionInfo/${location.id}`);
                    if (details && details.description) {
                        data.description = details.description;
                    } else {
                        console.error("No description found or incorrect data path in the second API call.");
                    }
                }

                return data;
            } catch (error) {
                console.error(error);
                throw error;
            }
        },
        {
            onSuccess: () => {
                options?.onSuccess && options.onSuccess();
            },
            onError: () => {
                queryClient.invalidateQueries([PROJECT_LINE_LOCATION_QUERY_KEY]);
                options?.onError && options.onError();
            },
            ...options,
        }
    );
};

// -----------------------------
// EQUIPMENT HOOKS & FUNCTIONS
// -----------------------------
async function fetchEquipmentById(equipmentId) {
    return apiGet(`/api/common/equipment/${equipmentId}`);
}
async function fetchGetEquipmentByNumber(equipmentNumber) {
    return apiGet(`/api/common/equipment/number/${equipmentNumber}`);
}
async function updateEquipmentGroup({ equipmentId, groupId }) {
    return apiPut("/api/common/equipment-groups", {
        EquipmentId: equipmentId,
        GroupId: groupId,
    });
}

export const useFetchEquipmentByIdQuery = (equipmentId, enabled = true) => {
    return useQuery([EQUIPMENT_QUERY_KEY, equipmentId], () => fetchEquipmentById(equipmentId), { enabled: enabled });
};
export const useFetchGetEquipmentByNumberQuery = (equipmentNumber) => {
    return useQuery([EQUIPMENT_QUERY_KEY, equipmentNumber], () => fetchGetEquipmentByNumber(equipmentNumber));
};
export const useFetchEquipmentByIdMutation = (options = {}) => {
    return useMutation(fetchEquipmentById, options);
};
export const useFetchGetEquipmentByNumberMutation = (options = {}) => {
    return useMutation(fetchGetEquipmentByNumber, options);
};
export const useUpdateEquipmentGroupMutation = (options = {}) => {
    const queryClient = useQueryClient();
    return useMutation(({ equipmentId, groupId }) => updateEquipmentGroup({ equipmentId, groupId }), {
        onSuccess: () => {
            queryClient.invalidateQueries([EQUIPMENT_QUERY_KEY]);
            options.onSuccess && options.onSuccess();
        },
        ...options,
    });
};

// -----------------------------
// INSPECTION HOOKS & FUNCTIONS
// -----------------------------
async function fetchInspectionLinesById(employeeId) {
    return apiGet(`/api/inspection/GetInspectionLines/${employeeId}`);
}
async function fetchInspectionLineById(inspectionLineId) {
    return apiGet(`/api/inspection/GetInspectionLineById/${inspectionLineId}`);
}
async function fetchAdminInspectionLines() {
    return apiGet("/api/inspection/GetAdminInspectionLines");
}
async function failInspection({ inspectionLineId, InspectorId }) {
    return apiPost("/api/inspection/FailInspection", {
        id: inspectionLineId,
        InspectorId: InspectorId,
    });
}
async function passInspection({ inspectionLineId, InspectorId }) {
    return apiPost("/api/inspection/PassInspection", {
        Id: inspectionLineId,
        InspectorId: InspectorId,
    });
}
async function updateInspectionLineComment({ inspectionLineId, comment }) {
    return apiPost("/api/inspection/UpdateInspectionLineComment", {
        Id: inspectionLineId,
        Comment: comment,
    });
}

export const useGetInspectionLinesByIdQuery = (employeeId) => {
    return useQuery([INSPECTION_LINES_QUERY_KEY, employeeId], () => fetchInspectionLinesById(employeeId));
};
export const useGetAdminInspectionLinesQuery = (isAdmin = false) => {
    return useQuery(ADMIN_INSPECTION_LINES_QUERY_KEY, fetchAdminInspectionLines, { enabled: isAdmin });
};
export const useFetchInspectionLineByIdQuery = (inspectionLineId) => {
    return useQuery([INSPECTION_LINE_QUERY_KEY, inspectionLineId], () => fetchInspectionLineById(inspectionLineId));
};
export const useFailInspectionMutation = (options = {}) => {
    return useMutation(({ inspectionLineId, InspectorId }) => failInspection({ inspectionLineId, InspectorId }), {
        ...options,
    });
};
export const usePassInspectionMutation = (options = {}) => {
    return useMutation(({ inspectionLineId, InspectorId }) => passInspection({ inspectionLineId, InspectorId }), {
        ...options,
    });
};
export const useUpdateInspectionLineCommentMutation = () => {
    return useMutation(({ inspectionLineId, comment }) => updateInspectionLineComment({ inspectionLineId, comment }));
};

// -----------------------------
// WORKSHOP HOOKS & FUNCTIONS
// -----------------------------
async function createSparePart({ ProductId, WorkshopOrderId, WorkshopLineId, EmployeeId, Quantity }) {
    return apiPost("/api/Workshop/CreateSparePart", {
        ProductId: ProductId,
        WorkshopOrderId: WorkshopOrderId,
        WorkshopLineId: WorkshopLineId,
        EmployeeId: EmployeeId,
        Quantity: Quantity,
    });
}
async function fetchSpareParts() {
    return apiGet(`/api/Workshop/GetSpareParts`);
}
async function fetchWorkshopProducts() {
    return apiGet(`/api/Workshop/GetProducts`);
}
async function fetchEmployeeWorkshopOrders(employeeId) {
    return apiGet(`/api/Workshop/GetEmployeeWorkshopOrders/${employeeId}`);
}
async function fetchWorkshopOrdersByEquipmentId(equipmentId) {
    return apiGet(`/api/Workshop/GetWorkshopOrdersByEquipmentId/${equipmentId}`);
}
async function fetchWorkshopOrderById(workshopOrderId) {
    return apiGet(`/api/Workshop/GetWorkshopOrderById/${workshopOrderId}`);
}
async function createWorkshopOrder({ Description, VehicleId, ReporterId }) {
    return apiPost("/api/Workshop/CreateWorkshopOrder", {
        Description: Description,
        VehicleId: VehicleId,
        ReporterId: ReporterId,
    });
}
async function fetchMostRecentWorkshopLine(workshopOrderId, employeeId) {
    return apiGet(`/api/Workshop/GetMostRecentWorkshopLine/${workshopOrderId}/${employeeId}`);
}
async function createWorkshopLine({ WorkshopOrderId, WorkerId, CheckIn }) {
    return apiPost("/api/Workshop/CreateWorkshopLine", {
        WorkshopOrderId: WorkshopOrderId,
        WorkerId: WorkerId,
        CheckIn: CheckIn,
    });
}
async function stopWorkshopLine({ WorkshopOrderId, WorkshopLineId, WorkerId, CheckOut }) {
    return apiPost("/api/Workshop/UpdateWorkshopLine", {
        WorkshopOrderId: WorkshopOrderId,
        WorkshopLineId: WorkshopLineId,
        WorkerId: WorkerId,
        CheckOut: CheckOut,
    });
}
async function updateWorkshopLineComment({ WorkshopOrderId, WorkshopLineId, WorkerId, Comment }) {
    return apiPost("/api/Workshop/UpdateWorkshopLine", {
        WorkshopOrderId: WorkshopOrderId,
        WorkshopLineId: WorkshopLineId,
        WorkerId: WorkerId,
        Comment: Comment,
    });
}
async function updateWorkshopLine({ WorkshopOrderId, WorkshopLineId, WorkerId, CheckOut = null, Comment = null }) {
    return apiPost("/api/Workshop/UpdateWorkshopLine", {
        WorkshopOrderId: WorkshopOrderId,
        WorkshopLineId: WorkshopLineId,
        WorkerId: WorkerId,
        CheckOut: CheckOut,
        Comment: Comment,
    });
}
async function deactivateWorkshopOrder(workshopOrderId) {
    return apiDelete(`/api/Workshop/DeactivateWorkshopOrder/${workshopOrderId}`, {});
}

export const useFetchSparePartsQuery = () => {
    return useQuery([SPARE_PARTS_QUERY_KEY], () => fetchSpareParts());
};
export const useFetchWorkshopProductsQuery = () => {
    return useQuery([WORKSHOP_PRODUCTS_QUERY_KEY], () => fetchWorkshopProducts());
};
export const useCreateSparePartMutation = (options = {}) => {
    return useMutation(
        ({ ProductId, WorkshopOrderId, WorkshopLineId, EmployeeId, Quantity }) =>
            createSparePart({ ProductId, WorkshopOrderId, WorkshopLineId, EmployeeId, Quantity }),
        { ...options }
    );
};
export const useFetchWorkshopOrderByIdQuery = (workshopOrderId, enabled = true) => {
    return useQuery([WORKSHOP_ORDER_QUERY_KEY, workshopOrderId], () => fetchWorkshopOrderById(workshopOrderId), {
        enabled: enabled,
    });
};
export const useFetchEmployeeWorkshopOrdersQuery = (employeeId) => {
    return useQuery([WORKSHOP_ORDERS_QUERY_KEY, employeeId], () => fetchEmployeeWorkshopOrders(employeeId));
};
export const useFetchWorkshopOrdersByEquipmentIdQuery = (equipmentId) => {
    return useQuery([WORKSHOP_ORDERS_QUERY_KEY, equipmentId], () => fetchWorkshopOrdersByEquipmentId(equipmentId));
};
export const useFetchMostRecentWorkshopLine = (workshopOrderId, employeeId, options = {}) => {
    return useQuery(
        [ACTIVE_WORKSHOP_LINE_QUERY_KEY, workshopOrderId, employeeId],
        () => fetchMostRecentWorkshopLine(workshopOrderId, employeeId),
        options
    );
};
export const useCreateWorkshopLineMutation = (options = {}) => {
    const queryClient = useQueryClient();
    return useMutation(
        ({ WorkerId, WorkshopOrderId, CheckIn }) => createWorkshopLine({ WorkerId, WorkshopOrderId, CheckIn }),
        {
            onSuccess: () => {
                queryClient.invalidateQueries([ACTIVE_WORKSHOP_LINE_QUERY_KEY]);
                options.onSuccess && options.onSuccess();
            },
            ...options,
        }
    );
};
export const useStopWorkshopLineMutation = (options = {}) => {
    const queryClient = useQueryClient();
    return useMutation(
        ({ WorkshopOrderId, WorkshopLineId, WorkerId, CheckOut }) =>
            stopWorkshopLine({ WorkshopOrderId, WorkshopLineId, WorkerId, CheckOut }),
        {
            onSuccess: () => {
                queryClient.invalidateQueries([ACTIVE_WORKSHOP_LINE_QUERY_KEY]);
                options.onSuccess && options.onSuccess();
            },
            ...options,
        }
    );
};
export const useUpdateWorkshopLineCommentMutation = (options = {}) => {
    const queryClient = useQueryClient();
    return useMutation(
        ({ WorkshopOrderId, WorkshopLineId, WorkerId, Comment }) =>
            updateWorkshopLineComment({ WorkshopOrderId, WorkshopLineId, WorkerId, Comment }),
        {
            onSuccess: () => {
                queryClient.invalidateQueries([ACTIVE_WORKSHOP_LINE_QUERY_KEY]);
                options.onSuccess && options.onSuccess();
            },
            ...options,
        }
    );
};
export const useUpdateWorkshopLineMutation = (options = {}) => {
    const queryClient = useQueryClient();
    return useMutation(
        ({ WorkshopOrderId, WorkshopLineId, WorkerId, CheckOut = null, Comment = null }) =>
            updateWorkshopLine({ WorkshopOrderId, WorkshopLineId, WorkerId, CheckOut, Comment }),
        {
            onSuccess: () => {
                queryClient.invalidateQueries([ACTIVE_WORKSHOP_LINE_QUERY_KEY]);
                options.onSuccess && options.onSuccess();
            },
            ...options,
        }
    );
};
export const useCreateWorkshopOrderMutation = (options = {}) => {
    return useMutation(
        ({ Description, VehicleId, ReporterId }) => createWorkshopOrder({ Description, VehicleId, ReporterId }),
        {
            ...options,
        }
    );
};
export const useDeactivateWorkshopOrderMutation = (options = {}) => {
    return useMutation((WorkshopOrderId) => deactivateWorkshopOrder(WorkshopOrderId), options);
};

// -----------------------------
// WAREHOUSE HOOKS & FUNCTIONS
// -----------------------------
async function fetchPickingLines(dueDate) {
    return apiGet(`/api/Warehouse/GetStoragePickingLines?dueDate=${dueDate}`);
}
async function fetchPickingLineById(pickingLineId) {
    return apiGet(`/api/Warehouse/GetStoragePickingLine/${pickingLineId}`);
}
async function fetchPickingLineExtraBoxContent(pickingLineId) {
    return apiGet(`/api/Warehouse/GetStoragePickingLineExtraBoxContent/${pickingLineId}`);
}
async function fetchPickingLineNewProducts(pickingLineId) {
    return apiGet(`/api/Warehouse/GetStoragePickingLineNewProducts/${pickingLineId}`);
}
async function fetchStockTally(addressLocationId) {
    return apiGet(`/api/Warehouse/GetStockTally/${addressLocationId}`);
}
async function createStockActivity({ addressLocationId, productId, quantity, name }) {
    return apiPost("/api/Warehouse/CreateStockActivity", {
        AddressLocationId: addressLocationId,
        ProductId: productId,
        Quantity: quantity,
        Name: name,
    });
}
async function updatePickingLine({ pickingLineId, numberOfBoxes, numberOfExtraBoxes, status, boxLocationName }) {
    return apiPost("/api/Warehouse/UpdateStoragePickingLine", {
        PickingLineId: pickingLineId,
        NumberOfBoxes: numberOfBoxes,
        NumberOfExtraBoxes: numberOfExtraBoxes,
        Status: status,
        BoxLocationName: boxLocationName,
    });
}
async function createPickingLineProduct({ projectId, productId, quantity, writtenOff, comment }) {
    return apiPost("/api/Warehouse/CreatePickingLineProduct", {
        ProjectId: projectId,
        ProductId: productId,
        Quantity: quantity,
        WrittenOff: writtenOff,
        Comment: comment,
    });
}

export const useFetchPickingLinesQuery = (dueDate, options = {}) => {
    return useQuery(
        [PICKING_LINES_QUERY_KEY, dueDate],
        () => fetchPickingLines(dueDate),
        {
            ...options,
            enabled: options.enabled ? options.enabled : true
        }
    );
};
export const useFetchPickingLineByIdQuery = (pickingLineId, options = {}) => {
    return useQuery([PICKING_LINE_QUERY_KEY, pickingLineId], () => fetchPickingLineById(pickingLineId), options);
};
export const useFetchfetchPickingLineExtraBoxContentQuery = (pickingLineId, options = {}) => {
    return useQuery(
        [PICKING_LINE_EXTRA_BOX_QUERY_KEY, pickingLineId],
        () => fetchPickingLineExtraBoxContent(pickingLineId),
        options
    );
};
export const useFetchfetchPickingLineNewProductsQuery = (pickingLineId, options = {}) => {
    return useQuery(
        [PICKING_LINE_NEW_PRODUCTS_QUERY_KEY, pickingLineId],
        () => fetchPickingLineNewProducts(pickingLineId),
        options
    );
};
export const useFetchStockTallyQuery = (addressLocationId, options = {}) => {
    return useQuery([STOCK_TALLY_QUERY_KEY, addressLocationId], () => fetchStockTally(addressLocationId), options);
};
export const useCreateStockActivityMutation = (options = {}) => {
    const queryClient = useQueryClient();
    return useMutation(
        ({ addressLocationId, productId, quantity, name }) => createStockActivity({ addressLocationId, productId, quantity, name }),
        {
            // options is spread first so this onSuccess is not replaced by a caller's own.
            ...options,
            onSuccess: (data, variables) => {
                // gar_stocktally is recalculated by an asynchronous CRM plugin, so refetching
                // here would read back the pre-alteration quantity and undo what the user just
                // did on screen. Apply the delta to the cached row instead.
                queryClient.setQueryData([STOCK_TALLY_QUERY_KEY, variables.addressLocationId], (rows) =>
                    rows?.map((row) =>
                        row.id === variables.stockTallyId
                            ? { ...row, quantity: Math.round((row.quantity + variables.quantity) * 100) / 100 }
                            : row
                    )
                );
                options.onSuccess && options.onSuccess(data, variables);
            },
        }
    );
};
export const useUpdatePickingLineMutation = (options = {}) => {
    const queryClient = useQueryClient();
    return useMutation(
        ({ pickingLineId, numberOfBoxes, numberOfExtraBoxes, status, boxLocationName }) =>
            updatePickingLine({ pickingLineId, numberOfBoxes, numberOfExtraBoxes, status, boxLocationName }),
        {
            onSuccess: () => {
                queryClient.invalidateQueries([PICKING_LINE_QUERY_KEY]);
                options.onSuccess && options.onSuccess();
            },
            ...options,
        }
    );
};
export const useCreatePickingLineProductMutation = (options = {}) => {
    const queryClient = useQueryClient();
    return useMutation(
        ({ projectId, productId, quantity, writtenOff, comment }) =>
            createPickingLineProduct({ projectId, productId, quantity, writtenOff, comment }),
        {
            onSuccess: () => {
                queryClient.invalidateQueries({ queryKey: [PICKING_LINE_NEW_PRODUCTS_QUERY_KEY] });
                options.onSuccess && options.onSuccess();
            },
            ...options,
        }
    );
};

// -----------------------------
// GARDENING ADMIN
// -----------------------------
async function fetchGardeningManagers() {
    return apiGet("/api/Gardening/GetManagers");
}
async function updateWorklineManager({ projectLineId, managerId }) {
    return apiPost("/api/Gardening/UpdateWorklineManager", {
        ProjectLineId: projectLineId,
        ManagerId: managerId,
    });
}
async function updateProjectDescription({ projectId, projectLineId, description }) {
    return apiPost("/api/Gardening/UpdateProjectDescription", {
        ProjectId: projectId,
        ProjectLineId: projectLineId,
        Description: description,
    });
}

export const useFetchGardeningManagersQuery = (options = {}) => {
    return useQuery([GARDENING_MANAGERS_QUERY_KEY], fetchGardeningManagers, options);
};
export const useCreateGardeningWorklineMutation = (options = {}) => {
    const queryClient = useQueryClient();
    return useMutation(
        ({ projectLineId, employeeId, hours, description }) => apiPost("/api/Gardening/CreateWorkline", {
            ProjectLineId: projectLineId,
            EmployeeId: employeeId,
            Hours: hours,
            Description: description,
        }),
        {
            ...options,
            onSuccess: (data, variables) => {
                queryClient.invalidateQueries([PROJECT_LINE_QUERY_KEY]);
                options.onSuccess && options.onSuccess(data, variables);
            },
        }
    );
};
export const useUpdateWorklineManagerMutation = (options = {}) => {
    const queryClient = useQueryClient();
    return useMutation(({ projectLineId, managerId }) => updateWorklineManager({ projectLineId, managerId }), {
        onSuccess: (data, variables) => {
            queryClient.invalidateQueries([PROJECT_LINE_QUERY_KEY, variables.projectLineId]);
            options.onSuccess && options.onSuccess(data, variables);
        },
        ...options,
    });
};
export const useUpdateProjectDescriptionMutation = (options = {}) => {
    const queryClient = useQueryClient();
    return useMutation(({ projectId, projectLineId, description }) => updateProjectDescription({ projectId, projectLineId, description }), {
        onSuccess: (data, variables) => {
            queryClient.invalidateQueries([PROJECT_LINE_QUERY_KEY]);
            options.onSuccess && options.onSuccess(data, variables);
        },
        ...options,
    });
};
